// Прив'язані шари: Popover (портал + fixed-позиція від якоря, закриття кліком поза ним і Esc), Dropdown, MenuItem.
// Портал не обрізається прокруткою бічних панелей і таблиць.
import type { LucideIcon } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type Ref,
  type RefObject,
} from 'react';
import { cn } from '@/lib/cn';
import { Portal } from './Dialog';

export interface AnchorOptions {
  align?: 'start' | 'end';
  /** Ширина = ширині якоря (комбобокс). */
  matchWidth?: boolean;
  offset?: number;
  /** Бажана максимальна висота шару. */
  maxHeight?: number;
}

/** Обчислює fixed-позицію шару під (або над, якщо бракує місця) якорем; оновлюється при прокрутці й зміні розміру. */
export function useAnchorPosition(anchorRef: RefObject<HTMLElement | null>, open: boolean, opts: AnchorOptions = {}): CSSProperties {
  const { align = 'start', matchWidth = false, offset = 4, maxHeight = 320 } = opts;
  const [style, setStyle] = useState<CSSProperties>({ position: 'fixed', top: -9999, left: -9999 });
  const compute = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const below = vh - r.bottom - offset - 8;
    const above = r.top - offset - 8;
    const openUp = below < Math.min(maxHeight, 200) && above > below;
    const mh = Math.max(120, Math.min(maxHeight, openUp ? above : below));
    const s: CSSProperties = { position: 'fixed', maxHeight: mh, zIndex: 60 };
    if (openUp) s.bottom = vh - r.top + offset;
    else s.top = r.bottom + offset;
    if (matchWidth) s.width = r.width;
    if (align === 'end') s.right = Math.max(8, vw - r.right);
    else s.left = Math.max(8, Math.min(r.left, vw - (matchWidth ? r.width : 240) - 8));
    setStyle(s);
  }, [anchorRef, align, matchWidth, offset, maxHeight]);

  useLayoutEffect(() => {
    if (!open) return;
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open, compute]);
  return style;
}

export interface PopoverProps extends AnchorOptions {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  className?: string;
  role?: string;
  id?: string;
  'aria-label'?: string;
}

export function Popover({ open, onClose, anchorRef, children, className, role, id, align, matchWidth, offset, maxHeight, ...rest }: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const style = useAnchorPosition(anchorRef, open, { align, matchWidth, offset, maxHeight });

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        anchorRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;
  return (
    <Portal>
      <div
        ref={panelRef}
        id={id}
        role={role}
        aria-label={rest['aria-label']}
        style={style}
        className={cn('overflow-y-auto rounded-field border border-border bg-surface p-1 shadow-popover animate-pop-in scroll-thin', className)}
      >
        {children}
      </div>
    </Portal>
  );
}

export interface DropdownTriggerProps {
  ref: Ref<HTMLButtonElement>;
  onClick: () => void;
  'aria-expanded': boolean;
  'aria-haspopup': 'menu';
}

export interface DropdownProps {
  /** Кнопка-тригер: передайте їй усі props (ref, onClick, aria-*). */
  trigger: (props: DropdownTriggerProps) => ReactNode;
  /** Вміст меню; close() — закрити після дії. */
  children: (close: () => void) => ReactNode;
  align?: 'start' | 'end';
  className?: string;
  'aria-label'?: string;
}

/** Випадне меню (профіль, дії рядка, вибір колонок). */
export function Dropdown({ trigger, children, align = 'end', className, ...rest }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      {trigger({ ref, onClick: () => setOpen((v) => !v), 'aria-expanded': open, 'aria-haspopup': 'menu' })}
      <Popover open={open} onClose={close} anchorRef={ref} align={align} role="menu" className={cn('min-w-52', className)} aria-label={rest['aria-label']}>
        {children(close)}
      </Popover>
    </>
  );
}

export interface MenuItemProps {
  icon?: LucideIcon;
  children: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Праворуч (Kbd, лічильник). */
  end?: ReactNode;
}

export function MenuItem({ icon: Icon, children, onSelect, danger, disabled, end }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-[8px] px-2.5 py-2 text-left text-[14px] transition-colors disabled:opacity-50',
        danger ? 'text-danger hover:bg-danger-soft' : 'text-fg hover:bg-surface-2',
      )}
    >
      {Icon ? <Icon className={cn('size-4 shrink-0', danger ? 'text-danger' : 'text-muted')} aria-hidden /> : null}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {end}
    </button>
  );
}

/** Заголовок / розділювач групи в меню. */
export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="px-2.5 pt-2 pb-1 text-[11px] font-semibold tracking-wide text-faint uppercase">{children}</div>;
}

export function MenuSeparator() {
  return <div className="my-1 border-t border-divider" role="separator" />;
}
